import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Eye,
  MousePointerClick,
  Pencil,
  CheckCircle2,
  TrendingDown,
  TrendingUp,
  ArrowRight,
  BarChart3,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGE_OPTIONS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "all", label: "All time" },
];

const STAGE_ICONS = {
  view: Eye,
  focus: MousePointerClick,
  fill: Pencil,
  submit: CheckCircle2,
};

const STAGE_TONE = {
  view: { bg: "bg-blue-50", icon: "text-blue-600", bar: "bg-blue-500" },
  focus: { bg: "bg-amber-50", icon: "text-amber-600", bar: "bg-amber-500" },
  fill: { bg: "bg-violet-50", icon: "text-violet-600", bar: "bg-violet-500" },
  submit: { bg: "bg-emerald-50", icon: "text-emerald-600", bar: "bg-emerald-500" },
};

function authHeader() {
  const t = localStorage.getItem("token") || localStorage.getItem("heyintake_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function Kpi({ icon: Icon, label, value, sub, tone = "text-zinc-700", testId }) {
  return (
    <Card
      className="rounded-2xl border-0 bg-white"
      style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-3 text-zinc-500 text-xs uppercase tracking-wider">
          {Icon && <Icon className={`w-4 h-4 ${tone}`} />}
          <span>{label}</span>
        </div>
        <div className="mt-2 text-3xl font-semibold text-zinc-900">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function FunnelStage({ stage, max }) {
  const tone = STAGE_TONE[stage.key] || STAGE_TONE.view;
  const Icon = STAGE_ICONS[stage.key] || Eye;
  const widthPct = max > 0 ? Math.max(2, Math.round((stage.count / max) * 100)) : 2;
  return (
    <div className="space-y-1.5" data-testid={`funnel-stage-${stage.key}`}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-zinc-700 font-medium">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${tone.bg}`}>
            <Icon className={`w-3.5 h-3.5 ${tone.icon}`} />
          </span>
          {stage.label}
        </div>
        <div className="text-zinc-700">
          <span className="font-semibold">{stage.count.toLocaleString()}</span>
          <span className="text-zinc-400 ml-2">{stage.rate_of_views}% of views</span>
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
        <div
          className={`h-full ${tone.bar} transition-[width] duration-500`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}

export default function FormAnalyticsDashboard({ formId, formName }) {
  const navigate = useNavigate();
  const [range, setRange] = useState("30d");
  const [funnel, setFunnel] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAggregate = !formId;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const headers = authHeader();
        const requests = [
          axios.get(
            `${API}/business/analytics/funnel`,
            { headers, params: { range, ...(formId ? { form_id: formId } : {}) } }
          ),
        ];
        if (isAggregate) {
          requests.push(
            axios.get(`${API}/business/analytics/forms-list`, { headers, params: { range } })
          );
        }
        const results = await Promise.all(requests);
        if (cancelled) return;
        setFunnel(results[0].data);
        if (isAggregate) setList(results[1].data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [range, formId, isAggregate]);

  const max = useMemo(
    () => (funnel?.metrics?.stages || []).reduce((m, s) => Math.max(m, s.count), 0),
    [funnel]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <CardContent className="p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <span data-testid="analytics-error">{String(error)}</span>
        </CardContent>
      </Card>
    );
  }

  const m = funnel?.metrics || {};

  return (
    <div className="space-y-6" data-testid="form-analytics-dashboard">
      {/* Header + Range toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-400">
            {isAggregate ? "All forms" : "Form"}
          </div>
          <h2 className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            {isAggregate ? "Aggregate analytics" : (formName || funnel?.form_name || "Form analytics")}
          </h2>
        </div>
        <div className="inline-flex bg-zinc-100 rounded-full p-1" data-testid="range-toggle">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setRange(opt.key)}
              data-testid={`range-${opt.key}`}
              className={`px-3.5 py-1.5 text-sm rounded-full transition-colors ${
                range === opt.key
                  ? "bg-white text-zinc-900 shadow-sm font-medium"
                  : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi
          icon={Eye}
          tone="text-blue-500"
          label="Total views"
          value={(m.total_views || 0).toLocaleString()}
          sub={`${(m.views || 0).toLocaleString()} unique · ${m.avg_views_per_visitor || 0}× per visitor`}
          testId="kpi-total-views"
        />
        <Kpi
          icon={CheckCircle2}
          tone="text-emerald-500"
          label="Submissions"
          value={(m.submissions || 0).toLocaleString()}
          sub={`${m.conversion_rate || 0}% conversion`}
          testId="kpi-submissions"
        />
        <Kpi
          icon={TrendingDown}
          tone="text-rose-500"
          label="Drop-off"
          value={`${m.drop_off_rate || 0}%`}
          sub={`${((m.views || 0) - (m.submissions || 0)).toLocaleString()} did not submit`}
          testId="kpi-dropoff"
        />
        <Kpi
          icon={TrendingUp}
          tone="text-violet-500"
          label="Fill → Submit"
          value={`${m.fill_to_submit_rate || 0}%`}
          sub={`${(m.abandon_after_fill || 0).toLocaleString()} abandoned mid-form`}
          testId="kpi-fill-to-submit"
        />
      </div>

      {/* Started metrics — both definitions side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Kpi
          icon={MousePointerClick}
          tone="text-amber-500"
          label="Started · First focus"
          value={(m.started_focus || 0).toLocaleString()}
          sub={`${m.focus_rate || 0}% of viewers tapped a field`}
          testId="kpi-focus"
        />
        <Kpi
          icon={Pencil}
          tone="text-violet-500"
          label="Started · First fill"
          value={(m.started_fill || 0).toLocaleString()}
          sub={`${m.fill_rate || 0}% of viewers actually typed`}
          testId="kpi-fill"
        />
      </div>

      {/* Funnel visualization */}
      <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <CardHeader>
          <CardTitle className="text-base">Funnel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pb-6">
          {(m.stages || []).map((stage) => (
            <FunnelStage key={stage.key} stage={stage} max={max} />
          ))}
          {(!m.stages || max === 0) && (
            <div className="text-sm text-zinc-500 py-4 text-center" data-testid="funnel-empty">
              No funnel data yet for this period. Once visitors interact with your form, you'll see it here.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Aggregate-only: list of forms */}
      {isAggregate && (
        <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Forms · click to drill in</CardTitle>
            <span className="text-xs text-zinc-400">{(list?.forms || []).length} total</span>
          </CardHeader>
          <CardContent className="p-0">
            {(list?.forms || []).length === 0 ? (
              <div className="px-6 py-10 text-sm text-zinc-500 text-center">
                You have no forms yet. Create one to start collecting analytics.
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100" data-testid="forms-list">
                {(list?.forms || []).map((f) => (
                  <li key={f.form_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/form-builder/${f.form_id}?tab=analytics`)}
                      data-testid={`forms-list-row-${f.form_id}`}
                      className="w-full flex items-center justify-between gap-4 px-6 py-4 hover:bg-zinc-50 transition-colors text-left"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 truncate">{f.name}</div>
                        <div className="text-xs text-zinc-500 mt-0.5 truncate">
                          {f.mode === "post_booking" ? "Intake" : "Lead"} · {(f.total_views ?? f.views).toLocaleString()} views ({f.views.toLocaleString()} unique) · {f.submissions.toLocaleString()} submissions
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="text-right">
                          <div className="text-sm font-semibold text-zinc-900">{f.conversion_rate}%</div>
                          <div className="text-[11px] uppercase tracking-wider text-zinc-400">conv.</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/form-builder/${f.form_id}?tab=analytics`);
                          }}
                        >
                          View
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Daily series — compact sparkline-style table for transparency */}
      {(funnel?.series || []).length > 0 && (
        <Card className="rounded-2xl border-0 bg-white" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <CardHeader>
            <CardTitle className="text-base">Daily activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="daily-series">
                <thead className="bg-zinc-50">
                  <tr className="text-zinc-500 text-xs uppercase tracking-wider">
                    <th className="text-left font-medium px-6 py-3">Day</th>
                    <th className="text-right font-medium px-6 py-3">Views</th>
                    <th className="text-right font-medium px-6 py-3">Focus</th>
                    <th className="text-right font-medium px-6 py-3">Fill</th>
                    <th className="text-right font-medium px-6 py-3">Submit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {funnel.series.map((row) => (
                    <tr key={row.day}>
                      <td className="px-6 py-2.5 text-zinc-700">{row.day}</td>
                      <td className="px-6 py-2.5 text-right tabular-nums">{row.view || 0}</td>
                      <td className="px-6 py-2.5 text-right tabular-nums">{row.focus || 0}</td>
                      <td className="px-6 py-2.5 text-right tabular-nums">{row.fill || 0}</td>
                      <td className="px-6 py-2.5 text-right tabular-nums font-medium">{row.submit || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
